import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { BoldText } from '../../components/Summary/assets/SummaryStyles';
import { PackageBox } from '../../components/Summary/assets/styles';
import SummaryItem from '../../components/Summary/components/SummaryItem';
import { Badge } from '../../components/ui-kit';
import Flex from '../../components/ui-kit/Flex';
import Text from '../../components/ui-kit/Text';
import { NOTIFICATION_TYPE, PACKAGE_CHARGE_TYPE } from '../../utilities/constants';
import { addNewTag } from '../../utilities/packagesUtils';
import colors from '../ui-kit/themes/colors';
import { editCopyByReplaceMultipleStrings } from '../utilities/copyFunctions';
import { BadgeWrapper, TitleWrapper, ValueWrapper } from './styles';
import * as COPY from './utilities/copy';

const PackageItem = ({ item, showNotification, newAddedPackageCode, notificationType }) => {
  const defaultCopyObject = {
    '<AMOUNT>': item.quantity,
    '<TITLE>': item.packageName
  };
  let pkgTitle = editCopyByReplaceMultipleStrings(COPY.SUMMARY_PACKAGE_TITLE, defaultCopyObject);
  if (item.packageCharge === PACKAGE_CHARGE_TYPE.personPerDay) {
    pkgTitle = item.packageName;
  }

  if (item.creditsTitle) {
    pkgTitle = item.creditsTitle;
  }
  return (
    <Flex>
      <ValueWrapper>
        <Text fontWeight="500" fontSize={0} color="deepLakeBlue.100">
          {pkgTitle}
        </Text>
      </ValueWrapper>
      {showNotification &&
        addNewTag(newAddedPackageCode, item.packageCode) &&
        notificationType === NOTIFICATION_TYPE.success && (
          <BadgeWrapper>
            <Badge caps={false} variant={Badge.VARIANTS.DEEP_ORANGE_100}>
              {COPY.SUMMARY_NEW_PACKAGES}
            </Badge>
          </BadgeWrapper>
        )}
    </Flex>
  );
};

/**
 * Component to display existing packages or additional packages details in cost summary for existing reservation.
 * @param {array} props.packages - List of packages selected for the stay.
 * @param {number} props.packageTotal - Total Amount of packages selected.
 * @param {array} props.additionalPackages - List of additional packages user select after the booking.
 * @param {function} props.additionalPackageRenderer - Renderer for the packages which allow user to edit and delete the packages newly added.
 * @param {bool} props.showNotification - Show package added notification in cost summary.
 * @param {string} props.notificationType - Show notification type as success or fail based on add package event result
 * @param {string} props.newAddedPackageCode - Package code received after addign packages to the existing reservation.
 * @param {function} props.getNormalizedNumber - Validate if value is a number and returns 2 decimal places number.
 * @param {function} props.getPackageAmount - Convert package amout value in float and returns 2 decimal places number.
 */
const PackagesSummary = ({
  showTitle,
  showTotal,
  packages,
  packageTotal,
  additionalPackages,
  additionalPackageRenderer,
  showNotification,
  notificationType,
  newAddedPackageCode,
  getNormalizedNumber,
  getPackageAmount,
  isInModal
}) => {
  const [hasReservationPackages, setHasReservationPackages] = useState(false);
  const [isPackageSelectedToAdd, setIsPackageSelectedToAdd] = useState(false);

  useEffect(() => {
    if (packages.length > 0) {
      setHasReservationPackages(true);
    }
    if (additionalPackages.length > 0) {
      setIsPackageSelectedToAdd(true);
    }
  }, [packages, additionalPackages]);

  const renderAdditionalPackageTitle = (additionalPackage, index) => {
    return (
      <SummaryItem
        key={index}
        labelTitle={COPY.SUMMARY_ADITIONAL_PACKAGE_TITLE}
        mb={2}
        leftColumn={<TitleWrapper>{additionalPackage && additionalPackage.title}</TitleWrapper>}
        rightColumn={
          <ValueWrapper>
            $
            {additionalPackage &&
              additionalPackage.packageAmount &&
              getNormalizedNumber(additionalPackage.packageAmount)}
          </ValueWrapper>
        }
      />
    );
  };

  const renderAdditionalPackage = (additionalPackage, index) => {
    if (!additionalPackage.isAdjustment) {
      if (additionalPackageRenderer) {
        return <React.Fragment key={index}>{additionalPackageRenderer(additionalPackage)}</React.Fragment>;
      }
      renderAdditionalPackageTitle(additionalPackage, index);
    }
    return null;
  };

  return (
    <PackageBox borderTop={'1px solid' + colors.snowMountainGrey[100]} mb={3} pt={3} color="deepLakeBlue.100">
      {(hasReservationPackages && showTitle) || (isPackageSelectedToAdd && !isInModal && showTitle) ? (
        <SummaryItem
          labelTitle={COPY.SUMMARY_PACKAGES}
          mb={1}
          pt={3}
          leftColumn={
            <BoldText caps>
              <Text fontSize={0}>{COPY.SUMMARY_PACKAGES}</Text>
            </BoldText>
          }
        />
      ) : null}
      {hasReservationPackages
        ? packages.map(
          (item, index) =>
            !item.isAdjustment && (
              <SummaryItem
                key={index}
                labelTitle={COPY.SUMAMRY_PACKAGES_AMOUNT}
                mb={2}
                leftColumn={
                  <PackageItem
                    item={item}
                    showNotification={showNotification}
                    notificationType={notificationType}
                    newAddedPackageCode={newAddedPackageCode}
                  />
                }
                rightColumn={
                  <ValueWrapper>
                    <Text fontWeight="500" fontSize={0} color="deepLakeBlue.100">
                      ${item && item.amount && getPackageAmount(item.amount)}
                    </Text>
                  </ValueWrapper>
                }
              />
            )
        )
        : null}
      {isPackageSelectedToAdd &&
        additionalPackages.map((additionalPackage, index) => {
          return renderAdditionalPackage(additionalPackage, index);
        })}
      {/* Handling show/hide for pacakge total */}
      {(hasReservationPackages && showTotal) || (isPackageSelectedToAdd && !isInModal && showTotal) ? (
        <SummaryItem
          labelTitle={COPY.SUMMARY_PACKAGES_TOTAL}
          mt={3}
          pb={3}
          leftColumn={
            <BoldText caps>
              <Text fontSize={0}>{COPY.SUMMARY_PACKAGES_TOTAL}</Text>
            </BoldText>
          }
          rightColumn={
            <BoldText>
              <Text fontSize={0}>${getNormalizedNumber(packageTotal)}</Text>
            </BoldText>
          }
        />
      ) : null}
    </PackageBox>
  );
};

/**
 * Setting default props for component
 */
PackagesSummary.defaultProps = {
  additionalPackages: [],
  packages: [],
  packageTotal: 0,
  showNotification: false
};

/**
 * Adding required props for component.
 */
PackagesSummary.propTypes = {
  packages: PropTypes.array,
  packageTotal: PropTypes.number,
  additionalPackages: PropTypes.array,
  additionalPackageRenderer: PropTypes.func,
  showNotification: PropTypes.bool
};

export default PackagesSummary;
